import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple, CheckCircle, Warning, X, Trash, MagnifyingGlass, Play,
  ArrowLeft, Sparkle, MapPin, CameraSlash, ImageSquare,
} from "@phosphor-icons/react";

/**
 * AdminNeedsDocsBatch — bulk photo/video → auto-match to Needs-Docs WOs.
 *
 * Flow:
 *   1. Drop / pick up to 30 files
 *   2. Backend extracts EXIF GPS + date, matches by distance, falls back to
 *      Claude Vision, returns per-file matches + a candidates list
 *   3. UI splits into two lanes:
 *        ✅ Auto-matched  — grouped by WO, admin can bump the match to a
 *                           different WO if needed
 *        ⚠️ Needs Review  — thumbnail row; pick a WO from a dropdown
 *   4. Commit — all decisions fire in one call → photos attach → WOs leave
 *      the Needs Docs queue.
 */

const TESTID = {
  page: "needs-docs-batch",
  dropzone: "batch-dropzone",
  filePicker: "batch-file-input",
  uploadBtn: "batch-upload-btn",
  clearBtn: "batch-clear-btn",
  commitBtn: "batch-commit-btn",
  reviewRow: "batch-review-row",
  matchedGroup: "batch-matched-group",
};

function fmtPct(v) { return Math.round((Number(v) || 0) * 100) + "%"; }

function ConfBadge({ conf, needsReview }) {
  if (needsReview) return <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest">Review</span>;
  if (conf >= 0.9) return <span className="px-1.5 py-0.5 bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest">High · {fmtPct(conf)}</span>;
  if (conf >= 0.7) return <span className="px-1.5 py-0.5 bg-lime-600 text-white text-[9px] font-black uppercase tracking-widest">Med · {fmtPct(conf)}</span>;
  return <span className="px-1.5 py-0.5 bg-slate-500 text-white text-[9px] font-black uppercase tracking-widest">Low · {fmtPct(conf)}</span>;
}

function Thumb({ item }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    let objectUrl = null;
    (async () => {
      try {
        const r = await api.get(`/needs-docs/batch-thumb/${item.stage_token}`, { responseType: "blob" });
        if (!live) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      } catch { /* fall back to broken img */ }
    })();
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [item.stage_token]);
  if (!url) {
    return <div className="w-full h-full bg-slate-200 animate-pulse"/>;
  }
  if (item.media_type === "video") {
    return (
      <div className="relative w-full h-full bg-slate-800">
        <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-8 w-8 bg-white/95 rounded-full flex items-center justify-center">
            <Play weight="fill" size={16} className="text-slate-900 ml-0.5"/>
          </div>
        </div>
      </div>
    );
  }
  return <img src={url} alt={item.filename} className="w-full h-full object-cover"/>;
}

function CandidatePicker({ candidates, value, onChange, testIdPrefix }) {
  return (
    <select
      value={value || ""}
      onChange={e => onChange(e.target.value || null)}
      data-testid={`${testIdPrefix}-candidate-picker`}
      className="w-full h-9 px-2 border-2 border-[#0f172a] bg-white text-xs font-mono">
      <option value="">— pick a WO —</option>
      {candidates.map(c => (
        <option key={c.id} value={c.id}>
          {c.address || c.title || c.id}
          {c.wo_number ? ` · WO ${c.wo_number}` : ""}
          {c.closed_day ? ` · ${c.closed_day}` : ""}
        </option>
      ))}
    </select>
  );
}

export default function AdminNeedsDocsBatch() {
  const [files, setFiles]         = useState([]);        // File[] queued before upload
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null);      // {items, candidates, count, auto_matched, needs_review}
  const [assignments, setAssignments] = useState({});    // token → {job_id, kind, skip}
  const [committing, setCommitting]   = useState(false);
  const inputRef = useRef(null);

  const totalConfident = useMemo(
    () => (result?.items || []).filter(i => i.stage_token && !i.needs_review && i.matched_job_id).length,
    [result],
  );
  const totalReview = useMemo(
    () => (result?.items || []).filter(i => i.stage_token && i.needs_review).length,
    [result],
  );

  const onFilesPicked = (list) => {
    const arr = Array.from(list || []).slice(0, 30);
    setFiles(arr);
  };

  const doUpload = async () => {
    if (!files.length) { toast.error("Pick some files first"); return; }
    setUploading(true);
    const t = toast.loading(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}…`);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f, f.name));
      const r = await api.post("/needs-docs/match-batch", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      const data = r.data;
      setResult(data);
      // Seed assignments from server matches
      const next = {};
      (data.items || []).forEach(i => {
        if (!i.stage_token) return;
        next[i.stage_token] = { job_id: i.matched_job_id || "", kind: "after", skip: false };
      });
      setAssignments(next);
      setFiles([]);
      toast.success(
        `${data.auto_matched} auto-matched · ${data.needs_review} need review`,
        { id: t, duration: 5000 },
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"), { id: t });
    } finally {
      setUploading(false);
    }
  };

  const setAssign = (token, patch) => {
    setAssignments(prev => ({ ...prev, [token]: { ...(prev[token] || {}), ...patch } }));
  };

  const commit = async () => {
    if (!result) return;
    const list = (result.items || [])
      .filter(i => i.stage_token)
      .map(i => ({
        stage_token: i.stage_token,
        job_id: assignments[i.stage_token]?.job_id || "",
        kind: assignments[i.stage_token]?.kind || "after",
        skip: !!assignments[i.stage_token]?.skip,
      }));
    const unresolved = list.filter(a => !a.skip && !a.job_id);
    if (unresolved.length) {
      if (!window.confirm(`${unresolved.length} file${unresolved.length === 1 ? " has" : "s have"} no WO assigned. Skip them and commit the rest?`)) return;
      unresolved.forEach(a => { a.skip = true; });
    }
    setCommitting(true);
    const t = toast.loading("Attaching photos to WOs…");
    try {
      const r = await api.post("/needs-docs/commit-batch", { assignments: list });
      const d = r.data;
      toast.success(
        `${d.committed} attached across ${d.affected_jobs} WO${d.affected_jobs === 1 ? "" : "s"}${d.skipped ? ` · ${d.skipped} skipped` : ""}`,
        { id: t, duration: 6000 },
      );
      setResult(null);
      setAssignments({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Commit failed"), { id: t });
    } finally {
      setCommitting(false);
    }
  };

  // Group auto-matched by WO for a cleaner review view.
  const groupedByJob = useMemo(() => {
    if (!result) return [];
    const buckets = new Map();
    (result.items || []).forEach(i => {
      if (!i.stage_token || i.needs_review) return;
      const jid = assignments[i.stage_token]?.job_id || i.matched_job_id;
      if (!jid) return;
      if (!buckets.has(jid)) buckets.set(jid, []);
      buckets.get(jid).push(i);
    });
    const candMap = new Map((result.candidates || []).map(c => [c.id, c]));
    return Array.from(buckets.entries()).map(([jid, items]) => ({
      job: candMap.get(jid) || { id: jid, address: "(unknown WO)", closed_day: "" },
      items,
    }));
  }, [result, assignments]);

  const reviewItems = useMemo(
    () => (result?.items || []).filter(i => i.stage_token && i.needs_review),
    [result],
  );
  const errorItems = useMemo(
    () => (result?.items || []).filter(i => i.error),
    [result],
  );

  return (
    <AdminShell>
      <div className="p-4 sm:p-8" data-testid={TESTID.page}>
        <div className="flex items-center gap-2 mb-2">
          <Link to="/admin/jobs?filter=needs-docs" className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft weight="bold" size={12}/> Needs Docs queue
          </Link>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Batch Photo Upload</h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Drop up to 30 phone photos or videos. AI matches each to a recently-closed WO using GPS + capture date,
          then falls back to vision on the address in the shot. Unmatched files land in a Review lane so you can drag them onto the right WO.
        </p>

        {/* Upload step */}
        {!result && (
          <div className="mt-6 rugged-card p-6" data-testid={TESTID.dropzone}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("bg-cyan-50","border-cyan-500"); }}
            onDragLeave={e => e.currentTarget.classList.remove("bg-cyan-50","border-cyan-500")}
            onDrop={e => {
              e.preventDefault(); e.currentTarget.classList.remove("bg-cyan-50","border-cyan-500");
              onFilesPicked(e.dataTransfer.files);
            }}>
            <div className="text-center">
              <UploadSimple weight="bold" size={48} className="mx-auto text-slate-400"/>
              <div className="mt-3 font-display font-black text-lg uppercase tracking-wide">
                Drop photos + videos here
              </div>
              <div className="text-xs text-slate-500 mt-1">or</div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                data-testid={TESTID.filePicker}
                onChange={e => onFilesPicked(e.target.files)}/>
              <button
                onClick={() => inputRef.current?.click()}
                data-testid="batch-file-picker-btn"
                className="mt-3 h-10 px-4 border-2 border-slate-900 bg-white hover:bg-slate-50 font-black uppercase text-xs tracking-widest">
                Choose files from device
              </button>
            </div>
            {files.length > 0 && (
              <div className="mt-6 border-t-2 border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                    {files.length} file{files.length === 1 ? "" : "s"} queued
                  </div>
                  <button onClick={() => setFiles([])} className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:underline">
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {files.map((f, i) => (
                    <div key={i} className="px-2 py-1 border border-slate-300 text-[11px] font-mono flex items-center gap-1">
                      {f.type.startsWith("video/") ? <Play weight="fill" size={10}/> : <ImageSquare weight="fill" size={10}/>}
                      <span className="truncate max-w-[160px]">{f.name}</span>
                      <span className="text-slate-400">{Math.round(f.size / 1024)}KB</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={doUpload}
                  disabled={uploading}
                  data-testid={TESTID.uploadBtn}
                  className="mt-4 h-11 px-5 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-60">
                  <Sparkle weight="fill" size={14}/>
                  {uploading ? "Matching photos…" : "Upload & match"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results step */}
        {result && (
          <div className="mt-6 space-y-6">
            {/* Stats bar */}
            <div className="rugged-card p-3 flex flex-wrap items-center gap-3">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                {result.count} file{result.count === 1 ? "" : "s"}
              </div>
              <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                <CheckCircle weight="fill" size={10}/> {result.auto_matched} auto-matched
              </span>
              <span className="px-2 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                <Warning weight="fill" size={10}/> {result.needs_review} need review
              </span>
              {result.errors > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                  <X weight="bold" size={10}/> {result.errors} error
                </span>
              )}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={() => { setResult(null); setAssignments({}); }}
                  data-testid={TESTID.clearBtn}
                  className="h-9 px-3 border-2 border-[#0f172a] text-[#0f172a] font-black uppercase text-xs tracking-widest hover:bg-slate-100">
                  Start over
                </button>
                <button
                  onClick={commit}
                  disabled={committing}
                  data-testid={TESTID.commitBtn}
                  className="h-9 px-4 bg-emerald-600 text-white font-black uppercase text-xs tracking-widest hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1">
                  <CheckCircle weight="bold" size={14}/>
                  {committing ? "Attaching…" : "Commit all"}
                </button>
              </div>
            </div>

            {/* Review lane FIRST (so it's not scrolled off screen) */}
            {reviewItems.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Warning weight="fill" size={16} className="text-amber-500"/>
                  <div className="font-display font-black text-lg uppercase tracking-wide">Needs Review</div>
                  <span className="text-[11px] font-bold text-slate-500">({reviewItems.length})</span>
                </div>
                <div className="rugged-card divide-y-2 divide-slate-100">
                  {reviewItems.map(i => (
                    <div key={i.stage_token} data-testid={TESTID.reviewRow}
                      className="flex items-center gap-3 p-3">
                      <div className="w-20 h-20 border-2 border-slate-300 overflow-hidden flex-shrink-0">
                        <Thumb item={i}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500 truncate">
                          {i.filename}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {i.captured_at && <span>📅 {i.captured_at.slice(0, 10)}</span>}
                          {i.gps ? <span className="inline-flex items-center gap-0.5"><MapPin weight="fill" size={10}/> GPS</span> : <span className="inline-flex items-center gap-0.5 text-slate-400"><CameraSlash weight="fill" size={10}/> no GPS</span>}
                          <span className="italic">{i.reason}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1">
                            <CandidatePicker
                              candidates={result.candidates}
                              value={assignments[i.stage_token]?.job_id}
                              onChange={val => setAssign(i.stage_token, { job_id: val })}
                              testIdPrefix={`review-${i.stage_token}`}/>
                          </div>
                          <button
                            onClick={() => setAssign(i.stage_token, { skip: true, job_id: "" })}
                            title="Drop this file from the batch"
                            className="h-9 px-2 border-2 border-red-500 text-red-600 font-black uppercase text-[10px] tracking-widest hover:bg-red-50">
                            <Trash weight="bold" size={12}/>
                          </button>
                        </div>
                        {assignments[i.stage_token]?.skip && (
                          <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-red-600">
                            → will be dropped on commit
                            <button className="ml-2 underline" onClick={() => setAssign(i.stage_token, { skip: false })}>undo</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-matched groups */}
            {groupedByJob.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <CheckCircle weight="fill" size={16} className="text-emerald-500"/>
                  <div className="font-display font-black text-lg uppercase tracking-wide">Auto-matched</div>
                  <span className="text-[11px] font-bold text-slate-500">({totalConfident} across {groupedByJob.length} WOs)</span>
                </div>
                <div className="space-y-3">
                  {groupedByJob.map(g => (
                    <div key={g.job.id} className="rugged-card p-3" data-testid={TESTID.matchedGroup}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <div className="font-black uppercase text-xs tracking-widest text-slate-800 truncate">
                            {g.job.address || g.job.title || g.job.id}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {g.job.wo_number ? `WO ${g.job.wo_number} · ` : ""}
                            closed {g.job.closed_day || "?"}
                          </div>
                        </div>
                        <Link to={`/admin/jobs/${g.job.id}`} className="text-[10px] font-black uppercase tracking-widest text-cyan-600 hover:underline flex items-center gap-1">
                          <MagnifyingGlass weight="bold" size={10}/> open WO
                        </Link>
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {g.items.map(i => (
                          <div key={i.stage_token} className="relative aspect-square border-2 border-slate-300 overflow-hidden group">
                            <Thumb item={i}/>
                            <div className="absolute top-1 left-1"><ConfBadge conf={i.confidence} needsReview={false}/></div>
                            <button
                              onClick={() => setAssign(i.stage_token, { job_id: "", skip: false })}
                              title="Wrong WO — move to review"
                              className="absolute top-1 right-1 h-5 w-5 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <X weight="bold" size={10}/>
                            </button>
                            <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] font-mono px-1 py-0.5 truncate">
                              {i.filename}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errorItems.length > 0 && (
              <div className="rugged-card p-3 border-red-500">
                <div className="font-black uppercase text-xs tracking-widest text-red-700 mb-1">Errors</div>
                <ul className="text-xs text-slate-700 list-disc list-inside">
                  {errorItems.map((e, i) => <li key={i}><span className="font-mono">{e.filename}</span> — {e.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
